import { useState, useEffect, useMemo, useCallback } from 'react';
import { NFLWaiverBadge } from '@/components/nfl/NFLWaiverBadge';
import { isOnWaivers as playerIsOnWaivers } from '@/lib/nflWaiverBadge';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useLeagueRosterability } from '@/hooks/useStartablePositions';
import { scopeChips, FLEX_CHIP, IDP_CHIP_GROUPS } from '@/lib/nflRosterability';
import { formatWeeklyProj } from '@/hooks/useWeeklyProjections';
import { useNflWeekGames } from '@/hooks/useNflWeekGames';
import { gameLine } from '@/lib/nflGameLine';
import NFLAddDropOverlay, { type AddDropTarget } from './NFLAddDropOverlay';
import { isRosterFull } from '@/lib/nflRosterSlots';
import { canAcquirePlayers, PRE_DRAFT_NOTICE } from '@/lib/nflDraftGate';
import NFLWaiverResults from './NFLWaiverResults';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UserSearch, Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Plus, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtInt, TONE_CLASS } from '@/components/nfl/nflStatsDefs';
import { AvailabilityPill } from './PlayerAvailability';
import { useToast } from '@/hooks/use-toast';
import NFLPlayerDetailModal from './NFLPlayerDetailModal';
import PlayerNewsIcon from '@/components/player/PlayerNewsIcon';
import { ActiveNewsProvider } from '@/components/player/ActiveNewsContext';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PlayerRow {
  id: string;
  full_name: string;
  position: string;
  nfl_franchise_id: string | null;
  nfl_team_abbr: string | null;
  headshot_url: string | null;
  status: string;
  injury_status: string | null;
  games_played: number;
  total_sabotage: number;
  total_trash: number;
  total_anti: number;
  avg_sabotage: number;
  // SAB-1176: the six stat keys the surface sorts on. Aggregated and ordered SERVER-SIDE
  // (migration 20260914200000) -- the list is paged, so a client-side sort on these would
  // order only the current page while the header claimed to sort the board.
  total_yds: number;
  total_td: number;
  total_int: number;
  total_fum: number;
  total_drp: number;
  total_tfl: number;
  /** SAB-994: this week's projection, joined server-side so it sorts and pages. */
  weekly_proj: number | null;
  // SAB-811: the shipped board. NULLABLE ON PURPOSE — IDP carry no projection for year one
  // and 86 board players have no basis, so these are null, never 0. Rendering a null as 0.0
  // is SAB-809, and here it would also sort as a real value.
  proj_sab_pts: number | null;
  adp: number | null;
  proj_source: string | null;
  owner_team_name: string | null;
  // SAB-1045: server-derived waiver designation. The client never computes these --
  // get_nfl_player_leaderboard reads the same nfl_waiver_settings() that nfl-add-drop's
  // refusal reads, so the badge and the refusal cannot disagree.
  waiver_state?: 'post_draft' | 'dropped' | null;
  waiver_clears_at?: string | null;
  // SAB-1033: RELEVANCE, not existence. False means the roster sync did not find this
  // player on any current NFL roster. He used to be filtered out of the pool entirely,
  // which made "cut" indistinguishable from "never existed" — including for players
  // someone already owns. The row is now returned and MARKED instead (SAB-843's rule:
  // the row is marked, never removed or reordered).
  active_2026?: boolean | null;
}

type SortKey = 'full_name' | 'position' | 'games_played' | 'total_sabotage' | 'avg_sabotage' | 'total_trash' | 'total_anti' | 'proj_sab_pts' | 'adp' | 'owner_team_name' | 'weekly_proj'
  | 'total_yds' | 'total_td' | 'total_int' | 'total_fum' | 'total_drp' | 'total_tfl';

/**
 * SAB-1176: the six stat keys the Players surface sorts on.
 *
 * `sortKey` is the SERVER's key (migration 20260914200000 added these to the RPC's
 * aggregation and its sort whitelist). The list is paged, so sorting these client-side
 * would order only the rows on screen.
 *
 * Tone follows the inverted model, same rule as My Team: trash keys score FOR you (damage
 * red), anti keys score AGAINST you (success green).
 */
/**
 * The row's ONLY actionable cell: owner name, waiver badge, or "+ Add".
 *
 * Extracted so the SAB-1045 rule can be tested against a fixture rather than hunted for in
 * a live league. It was inline, and the browser check for it was VACUOUS — the only league
 * reachable for verification was pre_draft, so no row rendered either control and a
 * mutation that deliberately showed both changed nothing.
 *
 * SAB-1045: the waiver designation REPLACES the plus, it never sits beside it. A row
 * offering both "+ Add" and "W / Fri" tells the manager two different things about the same
 * player, and the add the plus offers is one the server refuses.
 */
export function OwnerActionCell({
  player, myTeamName, canAcquire, onAcquire,
}: {
  player: PlayerRow;
  myTeamName: string | null;
  canAcquire: boolean;
  onAcquire: () => void;
}) {
  if (player.owner_team_name) {
    const mine = player.owner_team_name === myTeamName;
    return (
      <span className={cn(
        'text-[10px] font-medium truncate block max-w-[80px]',
        mine ? 'text-primary' : 'text-muted-foreground',
      )}>
        {mine ? 'My Team' : player.owner_team_name}
      </span>
    );
  }
  if (!canAcquire) return <span className="text-[10px] text-muted-foreground">—</span>;
  // Exclusive by construction: an early return, not a sibling.
  if (playerIsOnWaivers(player)) {
    return <NFLWaiverBadge player={player} onClick={onAcquire} />;
  }
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onAcquire(); }}
      className="flex items-center gap-0.5 text-[10px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
    >
      <Plus className="h-3 w-3" /> Add
    </button>
  );
}

const NFL_PLAYER_STAT_COLUMNS: {
  key: string; label: string; sortKey: SortKey; tone: 'trash' | 'anti';
  get: (p: PlayerRow) => number;
}[] = [
  { key: 'YDS', label: 'YDS', sortKey: 'total_yds', tone: 'anti',  get: p => p.total_yds },
  { key: 'TD',  label: 'TD',  sortKey: 'total_td',  tone: 'anti',  get: p => p.total_td },
  { key: 'INT', label: 'INT', sortKey: 'total_int', tone: 'trash', get: p => p.total_int },
  { key: 'FUM', label: 'FUM', sortKey: 'total_fum', tone: 'trash', get: p => p.total_fum },
  { key: 'DRP', label: 'DRP', sortKey: 'total_drp', tone: 'trash', get: p => p.total_drp },
  { key: 'TFL', label: 'TFL', sortKey: 'total_tfl', tone: 'trash', get: p => p.total_tfl },
];

type Availability = 'available' | 'taken' | 'all';
type SortDir = 'asc' | 'desc';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/**
 * The full chip set. What this league actually SHOWS is `positionFilters` below, scoped by
 * SAB-995 -- a chip for a position the league can never start returns nothing on every
 * click and reads as "no such players" rather than as a misconfigured filter.
 *
 * FLEX is appended rather than listed here because it is not a position: see FLEX_CHIP.
 */
const POSITION_FILTERS = ['All', 'QB', 'RB', 'WR', 'TE', 'K', 'P', 'DST', 'COACH', 'DB', 'LB', 'DL'];
const PAGE_SIZE = 50;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface NFLPlayersPageProps {
  /**
   * SAB-1062: opens the propose-trade flow pre-targeted at a team, optionally with a player
   * pre-added to their side. Threaded from NFLStaging so every surface that can open the
   * player-detail modal can also act on its Trade button -- a surface that cannot supply
   * this simply does not render the button, rather than rendering a dead one.
   */
  onTradeWith?: (teamId: string, playerId?: string) => void;
}

export default function NFLPlayersPage({ onTradeWith }: NFLPlayersPageProps = {}) {
  const { currentLeague, currentTeam, currentWeek, season, isLoading: contextLoading } = useNFLLeague();
  // SAB-995 / SAB-998: one derivation, shared with the draft room and the mobile page.
  const { startable, hasFlex, flex } = useLeagueRosterability(currentLeague?.settings?.roster_slots);
  const positionFilters = useMemo(() => {
    const scoped = scopeChips(POSITION_FILTERS, startable, {
      alwaysKeep: ['All'],
      // DB / LB / DL are the chip labels; the eligibility table also carries CB, S, DE and
      // the rest. Treating each chip as the group it displays keeps a league with any IDP
      // slot from losing all three chips because no player is literally positioned "DL".
      groupOf: (c) => (IDP_CHIP_GROUPS[c] ?? null),
    });
    // Gated on `flex` having loaded, not just on hasFlex. The chip needs the position SET
    // to filter with, and a chip that is present but cannot yet answer is worse than one
    // that appears alongside the rest a moment later.
    return hasFlex && flex !== null ? [...scoped, FLEX_CHIP] : scoped;
  }, [startable, hasFlex, flex]);
  const { toast } = useToast();

  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [posFilter, setPosFilter] = useState('All');
  // SAB-993: opens on AVAILABLE, not All. Post-draft the primary use of this page is
  // free-agent browsing; a rostered player is the exception you switch to. Adam's call.
  const [availability, setAvailability] = useState<Availability>('available');
  const [sortKey, setSortKey] = useState<SortKey>('total_sabotage');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);

  // Player detail modal
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null);
  // SAB-1172: which tab the detail sheet opens on. Only the news badge sets it.
  const [detailTab, setDetailTab] = useState<'stats' | 'news' | undefined>(undefined);

  // Add/Drop state
  const [addModal, setAddModal] = useState<PlayerRow | null>(null);
  const [myRoster, setMyRoster] = useState<{ player_id: string; full_name: string; position: string; position_slot: string }[]>([]);

  /**
   * SAB-1016: the overlay replaces the hand-rolled `fixed inset-0` card and its native
   * <select> of bare names. Two things were wrong beyond the styling: capacity was the
   * literal 21 (the DEFAULT template's size — League of Trash is 18), and the drop list
   * offered every non-IR player with no indication that most of them would be refused.
   */
  const [addDropTarget, setAddDropTarget] = useState<AddDropTarget | null>(null);
  // SAB-1017: which mode the overlay opens in. Set together with the target — an overlay
  // opened in the wrong mode would submit the wrong action against the server.
  const [overlayMode, setOverlayMode] = useState<'add' | 'bid'>('add');
  // SAB-1038: collapsed by default. The results log is reference material, not the reason
  // someone opened the Players tab, and an always-open list of every past run would push
  // the pool below the fold.
  const [showResults, setShowResults] = useState(false);

  // SAB-986: IR is a ghost slot on BOTH sides of the comparison. The old code counted IR
  // players toward 21 while filtering them out of the drop list, so a team with someone on
  // IR was told it was full and then offered nobody to drop.
  const rosterFull = useMemo(
    () => isRosterFull(currentLeague?.settings?.roster_slots, myRoster.map(r => r.position_slot)),
    [currentLeague?.settings?.roster_slots, myRoster],
  );
  const [dropPlayerId, setDropPlayerId] = useState<string>('');
  const [faabBid, setFaabBid] = useState(0);
  const [faabBudget, setFaabBudget] = useState(100);
  const [isOnWaivers, setIsOnWaivers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // SAB-1016 FIX: the roster must load WITH THE PAGE, not when the modal opens.
  //
  // This effect used to be gated on `addModal`, which was correct while its only consumer
  // was the modal's own drop <select>. `rosterFull` then made myRoster load-bearing for the
  // Add button's ROUTING -- and on the first click myRoster was still [], so rosterFull was
  // false and every Add fell through to the modal. The overlay was unreachable on desktop.
  //
  // Nothing failed: no error, no empty state, just the old modal where the new overlay
  // should have been. It took driving the real page in a browser to see it -- the component
  // tests render the overlay directly and so never exercise the gate that opens it.
  //
  // The waiver lookup below still needs `addModal` and keeps its own guard.
  useEffect(() => {
    if (!currentTeam || !currentLeague) return;
    let cancelled = false;

    async function load() {
      // FAAB
      const { data: team } = await supabase
        .from('nfl_teams')
        .select('faab_budget')
        .eq('id', currentTeam!.id)
        .single();
      if (!cancelled) setFaabBudget(Number((team as any)?.faab_budget) || 0);

      // Roster with player names
      const { data: roster } = await supabase
        .from('nfl_rosters')
        .select('player_id, position_slot')
        .eq('team_id', currentTeam!.id)
        .eq('league_id', currentLeague!.id)
        .eq('is_active', true);

      if (roster && !cancelled) {
        const pIds = roster.map(r => r.player_id);
        const { data: pData } = await supabase
          .from('nfl_players')
          .select('id, full_name, position')
          .in('id', pIds);
        const pMap: Record<string, any> = {};
        for (const p of pData || []) pMap[p.id] = p;
        setMyRoster(roster.map(r => ({
          player_id: r.player_id,
          full_name: pMap[r.player_id]?.full_name || '?',
          position: pMap[r.player_id]?.position || '?',
          position_slot: r.position_slot,
        })));
      }

      // Per-player, so it only runs once a player is actually chosen.
      if (!addModal) return;

      // SAB-1045: the row carries the server's answer, so the modal reads it instead of
      // re-running the drop-lock query. The old lookup missed the post-draft window
      // entirely and would label a claim-only player "Add Free Agent" -- and then send an
      // add body the server refused.
      if (!cancelled) setIsOnWaivers(playerIsOnWaivers(addModal));
    }
    load();
    return () => { cancelled = true; };
  }, [addModal, currentTeam, currentLeague]);

  /**
   * SAB-1016: which acquisition UI a click opens.
   *
   * A roster with room keeps the one-tap modal. A full roster gets the overlay — EXCEPT
   * for a player inside the waiver window, who needs bid mode (still the modal, until
   * SAB-1017 lands it in the overlay). That check has to happen here rather than in the
   * row, because waiver status is not on the leaderboard row: it is a recent-drop lookup,
   * which is why the modal has always resolved `isOnWaivers` in an effect after opening.
   *
   * nfl-add-drop refuses a waivered instant-add server-side (index.ts:157-167), so getting
   * this wrong costs a dead-end, not a bypassed waiver period — but the dead end is exactly
   * the toast-shaped failure this ticket exists to remove.
   */
  // SAB-1045: dropLockMs() lived here -- a client-side copy of the drop-lock rule used to
  // decide bid-vs-add. Both of its callers now read waiver_state off the leaderboard row,
  // which the server derives from the same nfl_waiver_settings() its refusal uses, so the
  // copy is gone rather than left unreferenced for someone to wire back up.

  // SAB-1024: pre-draft is VIEW-ONLY. Browsing, search and projections stay — scouting is
  // the pre-draft use of this tab; only acquisition is withheld.
  const canAcquire = canAcquirePlayers(currentLeague?.draft_status);

  const openAcquisition = async (p: PlayerRow) => {
    // Belt as well as braces: the buttons are not rendered below, but this is the single
    // door into both acquisition UIs and a future caller should not be able to walk through
    // it pre-draft either.
    if (!canAcquire) return;
    if (p.owner_team_name || !currentLeague || !currentTeam) { setAddModal(p); return; }

    // ─── SAB-1045: THE SERVER ALREADY ANSWERED THIS ─────────────────────
    //
    // This used to re-derive waiver status in the browser: read drop_lock_days, query
    // nfl_roster_transactions for a drop inside the window, and route on the result. Three
    // things were wrong with it, and the badge made them visible.
    //
    //  1. It knew nothing about the POST-DRAFT WINDOW (SAB-1023). Inside the window every
    //     unrostered player is claim-only, and this routed all of them to 'add' -- straight
    //     into a 409 the UI had just told the manager would not happen.
    //  2. The `!full` short-circuit below ran FIRST, so on a roster with space a waivered
    //     player opened "Add Free Agent" regardless. Browser-driving the badge is what
    //     surfaced this; it is why tapping "W / tmrw" offered a free-agent add.
    //  3. It was a second copy of the rule. SAB-1035 deleted one of those for drifting from
    //     the server's; this was the other one.
    //
    // get_nfl_player_leaderboard now returns the answer, computed from the same
    // nfl_waiver_settings() that nfl-add-drop refuses on. Routing on it is not an
    // optimisation -- it is what makes the badge and the flow the same authority.
    if (playerIsOnWaivers(p)) {
      setOverlayMode('bid');
      setAddDropTarget({
        id: p.id, full_name: p.full_name, position: p.position,
        nfl_team_abbr: p.nfl_team_abbr, headshot_url: p.headshot_url,
        status: p.status, injury_status: p.injury_status,
      });
      return;
    }

    // Not on waivers. Resolve fullness ON DEMAND rather than trusting `myRoster` to have
    // arrived: the page-load fetch is a race with the user, and clicking Add before it
    // settled read myRoster as [] -> rosterFull false -> the old modal, on a roster that is
    // in fact full. Browser-driving it is what exposed that too.
    let full = rosterFull;
    if (myRoster.length === 0) {
      const { data: rows } = await supabase
        // `as never`: multi-table conditional types resolve to SelectQueryError otherwise,
        // and every property read then scores on the ratchet. Same hatch as the sibling reads.
        .from('nfl_rosters' as never)
        .select('position_slot')
        .eq('team_id', currentTeam.id)
        .eq('league_id', currentLeague.id)
        .eq('is_active', true);
      const slots = ((rows ?? []) as unknown as { position_slot: string }[]).map(r => r.position_slot);
      // An empty result is a genuinely empty roster, not a failed read we should guess past:
      // isRosterFull() returns false for it, which routes to the modal -- the safe default.
      full = isRosterFull(currentLeague?.settings?.roster_slots, slots);
    }
    if (!full) { setAddModal(p); return; }

    setOverlayMode('add');
    setAddDropTarget({
      id: p.id, full_name: p.full_name, position: p.position,
      nfl_team_abbr: p.nfl_team_abbr, headshot_url: p.headshot_url,
      // SAB-1030: the IR affordance reads these. The server re-checks against the DB, so a
      // missing value here only costs the affordance, never a wrong placement.
      status: p.status, injury_status: p.injury_status,
    });
  };

  const handleAddOrClaim = async () => {
    if (!addModal || !currentTeam || !currentLeague) return;
    setSubmitting(true);

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) { toast({ title: 'Error', description: 'Not authenticated', variant: 'destructive' }); return; }

      const isWaiver = isOnWaivers;
      const body: Record<string, unknown> = isWaiver
        ? { action: 'claim', team_id: currentTeam.id, player_id: addModal.id, bid_amount: faabBid, drop_player_id: dropPlayerId }
        : { action: 'add', team_id: currentTeam.id, player_id: addModal.id, drop_player_id: dropPlayerId || undefined };

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-add-drop`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();

      if (data.error) {
        toast({ title: 'Error', description: data.error, variant: 'destructive' });
      } else {
        toast({ title: isWaiver ? 'Claim Submitted' : 'Player Added', description: data.message });
        setAddModal(null);
        setDropPlayerId('');
        setFaabBid(0);
        fetchPlayers(); // refresh
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [posFilter, debouncedSearch, sortKey, sortDir, availability]);

  // Fetch players via server-side RPC
  const fetchPlayers = useCallback(async () => {
    if (!currentLeague) { setIsLoading(false); return; }
    setIsLoading(true);
    setFetchError(null);

    try {
      const { data, error } = await supabase.rpc('get_nfl_player_leaderboard' as any, {
        p_league_id: currentLeague.id,
        // SAB-998: FLEX is a slot, not a position, so it must not go through p_position --
        // there is no player whose position is "FLEX" and the chip would return nothing.
        // It travels as the eligible SET instead, filtered server-side because the RPC
        // paginates (filtering after pagination is the bug SAB-993 removed).
        p_position: posFilter === 'All' || posFilter === FLEX_CHIP ? null : posFilter,
        p_positions: posFilter === FLEX_CHIP && flex ? [...flex] : null,
        // SAB-994: the week the WK column shows AND sorts on. Server-side, so ordering and
        // pagination stay consistent -- sorting a client-side lookup would have reordered
        // only the rows already on screen.
        p_week: currentWeek ?? null,
        p_search: debouncedSearch || null,
        p_sort_key: sortKey,
        p_sort_dir: sortDir,
        p_limit: PAGE_SIZE,
        p_offset: page * PAGE_SIZE,
        // null = all. The RPC keys this on the roster row that also produces
        // owner_team_name, so the filter and the Owner column cannot disagree.
        p_availability: availability === 'all' ? null : availability,
      });

      if (error) throw error;
      const result = data as any;
      setPlayers((result?.players || []) as PlayerRow[]);
      setTotalCount(result?.total || 0);
    } catch (err) {
      console.error('[NFLPlayersPage] Failed to fetch players:', err);
      Sentry.captureException(err, { tags: { component: 'NFLPlayersPage', op: 'fetchPlayers' } });
      setFetchError(err instanceof Error ? err : new Error(String(err)));
      setPlayers([]);
      setTotalCount(0);
    }
    setIsLoading(false);
  }, [currentLeague, posFilter, debouncedSearch, sortKey, sortDir, page, loadAttempt, availability, flex, currentWeek]);

  // True once ANY listed player has a game in the requested season. Derived from the rows
  // on screen rather than from a season constant, so it needs no knowledge of when week 1
  // is and cannot drift out of date.
  const seasonHasData = useMemo(() => players.some(p => (p.games_played ?? 0) > 0), [players]);

  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  // 10-second "still loading" toast for slow connections
  useEffect(() => {
    if (!isLoading) return;
    const t = setTimeout(() => {
      toast({
        title: 'Still loading…',
        description: 'Fetching players over a slow connection. Hang tight.',
      });
    }, 10000);
    return () => clearTimeout(t);
  }, [isLoading, loadAttempt, toast]);

  // This week's games, keyed by franchise: bye detection AND the opponent on the row's
  // sub-line. The shared read replaces this page's own copy of the (franchise, week) map;
  // its filter rationale (SAB-811 season_year, SAB-746 season_type) lives in the hook.
  // Behaviour change on a failed read: the old copy treated "no games came back" as "every
  // franchise is on bye" and badged the whole list BYE; the hook throws instead, so a
  // failure shows no BYE chips and no game line rather than a false one on every row.
  const weekGames = useNflWeekGames(currentWeek, season, !!currentLeague);

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      // SAB-811: 'adp' joins the ascending-first group. For ADP, LOWER IS BETTER — ADP 1 is
      // the first pick — so defaulting it to desc would open on the worst players and read
      // as broken. proj_sab_pts stays desc-first, where higher is better.
      setSortDir(key === 'full_name' || key === 'position' || key === 'adp' ? 'asc' : 'desc');
    }
  };

  if (contextLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentLeague) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <UserSearch className="h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Join a league to browse players</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-display font-bold text-foreground">Players</h1>
        <span className="flex items-center gap-3">
          <button
            type="button"
            data-testid="toggle-waiver-results"
            onClick={() => setShowResults(v => !v)}
            className="text-[10px] font-medium text-primary hover:text-primary/80"
          >
            {showResults ? 'Hide waiver results' : 'Waiver results'}
          </button>
          <span className="text-[10px] text-muted-foreground">{totalCount} players</span>
        </span>
      </div>

      {/* Search + Filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or team..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {/* SAB-993: availability. Sits with the position pills because it is the same kind
            of choice -- narrowing the list -- and because the two are read together when
            hunting a free agent at a position. */}
        <div className="flex flex-wrap gap-1.5">
          {([['available', 'Available'], ['taken', 'Taken'], ['all', 'All']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setAvailability(key)}
              className={cn(
                'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider transition-colors',
                availability === key
                  ? 'bg-primary/10 text-primary border border-primary/20'
                  : 'text-muted-foreground hover:text-foreground border border-transparent'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* SAB-993: the stat columns are empty until week 1 of the live season, and a
            grid of "--" reads as broken rather than as "not started". Adam's decision was
            no 2025 backfill and no season selector, so the honest fix is to SAY why the
            columns are blank and point at the numbers that ARE populated. Shown only when
            the page genuinely has no season data -- once week 1 lands it disappears on its
            own with no deploy. */}
        {/* SAB-1038: the league's waiver results live here, on the tab where acquisition
            happens. Resolved claims only — the RPC cannot return a pending row. */}
        {showResults && <NFLWaiverResults leagueId={currentLeague?.id ?? null} />}

        {/* SAB-1024: says WHY the Add column is empty. A tab that silently drops its
            primary action reads as broken; one that says when it comes back does not. */}
        {!canAcquire && (
          <p data-testid="pre-draft-notice" className="text-[11px] text-amber-300 leading-snug">
            {PRE_DRAFT_NOTICE}
          </p>
        )}

        {!isLoading && players.length > 0 && !seasonHasData && (
          <p className="text-[11px] text-muted-foreground leading-snug">
            Week 1 is underway. <span className="text-foreground/70">PROJ</span> and{' '}
            <span className="text-foreground/70">ADP</span> are live for every player; GP, SPTS,
            AVG, TRS and ANT populate once a player's own game has kicked off.
          </p>
        )}

        {/* Position pills */}
        <div className="flex flex-wrap gap-1.5">
          {positionFilters.map(pos => (
            <button
              key={pos}
              onClick={() => setPosFilter(pos)}
              className={cn(
                'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider transition-colors',
                posFilter === pos
                  ? 'bg-primary/10 text-primary border border-primary/20'
                  : 'text-muted-foreground hover:text-foreground border border-transparent'
              )}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : fetchError ? (
        <Card className="bg-destructive/5 border-destructive/30 p-6 text-center space-y-3">
          <p className="text-sm font-semibold text-destructive">Failed to load players.</p>
          <p className="text-xs text-muted-foreground">
            {fetchError.message || 'A network or server error occurred.'}
          </p>
          <Button size="sm" onClick={() => setLoadAttempt(n => n + 1)}>
            Tap to retry
          </Button>
        </Card>
      ) : (
        <Card className="bg-card border-border overflow-hidden">
          {/* Mobile hint: detailed stats are gated behind the row tap (opens
              NFLPlayerDetailModal). Keeps the mobile list scannable instead
              of horizontally scrolling through 8 columns. */}
          <p className="md:hidden px-4 py-2 text-[10px] text-muted-foreground border-b border-border/50">
            Tap a player for full stats
          </p>
          {/* overflow-x-auto is the SAFETY NET, not the fix.
              The ancestor Card is overflow-hidden, so before this the table did not merely
              run off-screen -- it was CLIPPED with no scrollbar offered, and the last column
              (Owner: +Add / waiver badge / ownership) became unreachable rather than merely
              inconvenient. The real fix is the lg: breakpoint above: at md (768px) all 11
              columns needed ~790px and could not fit, so 768-820px was a dead band where the
              only actionable control silently vanished. This keeps a residual overflow (long
              team names, browser zoom, larger default font) recoverable instead of invisible. */}
          <div className="overflow-x-auto">
            <ActiveNewsProvider sport="nfl" keys={players.map(p => p.id)}>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  {/* Player is the only unconstrained column, so table-auto hands it all the
                      slack. Measured at 768px it took 340px of a 712px container while Owner --
                      the only ACTIONABLE column -- was squeezed to 66px, under its declared w-24,
                      and pushed out of view. Capping it is what makes the row fit; the lg:
                      breakpoint below and overflow-x-auto handle the rest. */}
                  <th className="text-left px-3 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium max-w-[180px] lg:max-w-none xl:min-w-[200px]">
                    <SortButton label="Player" sortKey="full_name" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  {/* Owner sits at POSITION 2, next to the name, because it is the only
                      ACTIONABLE column and it was 242px past the right edge of a 952px
                      container at both 1280 and 1440 -- the widths where the six stat
                      columns show. See the scar at the overflow-x-auto comment below: this
                      control became unreachable once before, `overflow-x-auto` was added as
                      the safety net rather than the fix, and SAB-1176's six columns pushed
                      it straight back out. The net held (it scrolled instead of clipping)
                      and the problem returned anyway.

                      Moving it costs no width -- it is 66px wherever it sits -- so the same
                      242px still scrolls. What changes is WHICH columns scroll off: TFL /
                      DRP / FUM, which a manager sorts by, rather than the one they act on.
                      Position 2 is also what stops the next column addition recreating
                      this. */}
                  <th className="text-left px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-24">
                    <SortButton label="Owner" sortKey="owner_team_name" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  <th className="text-center px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-14">
                    <SortButton label="Pos" sortKey="position" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  {/* SAB-811: the shipped board. Deliberately NOT hidden at small widths --
                      these are the numbers a draft is run off, so they stay visible while
                      last season's stat columns are the ones that drop away. */}
                  <th className="text-right px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-16">
                    <SortButton label="PROJ" sortKey="proj_sab_pts" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  {/* SAB-994: weekly projection. Sortable, and sorted SERVER-SIDE via
                      p_week + the weekly_proj sort key -- sorting a client-side lookup
                      would have reordered only the 50 rows on screen while the header
                      claimed to sort the board. */}
                  <th className="text-right px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-16">
                    <SortButton label="WK" sortKey="weekly_proj" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  <th className="text-right px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-14">
                    <SortButton label="ADP" sortKey="adp" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  <th className="hidden lg:table-cell text-center px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-12">
                    <SortButton label="GP" sortKey="games_played" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  <th className="text-right px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-16">
                    <SortButton label="SPTS" sortKey="total_sabotage" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  <th className="hidden sm:table-cell text-right px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-16">
                    <SortButton label="AVG" sortKey="avg_sabotage" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  <th className="hidden lg:table-cell text-right px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-16">
                    <SortButton label="TRS" sortKey="total_trash" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  <th className="hidden lg:table-cell text-right px-2 py-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium w-16">
                    <SortButton label="ANT" sortKey="total_anti" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  </th>
                  {/* SAB-1176: the six stat keys — how a manager finds trash production to
                      claim. Header TONE carries the inverted model: trash keys damage-red,
                      anti keys success-green, so the sort you want is findable by colour.
                      Sorted server-side; see the SortKey union above. */}
                  {NFL_PLAYER_STAT_COLUMNS.map(c => (
                    <th key={c.key}
                        className={cn('hidden xl:table-cell text-right px-2 py-2 text-[10px] uppercase tracking-wider font-medium w-[46px]',
                                      TONE_CLASS[c.tone])}>
                      <SortButton label={c.label} sortKey={c.sortKey} current={sortKey} dir={sortDir} onClick={toggleSort} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {players.length === 0 ? (
                  <tr>
                    <td colSpan={17} className="text-center py-10 text-sm text-muted-foreground">
                      No players match your filters
                    </td>
                  </tr>
                ) : (
                  players.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => setDetailPlayerId(p.id)}
                      className={cn(
                        'border-b border-border/50 transition-colors hover:bg-muted/50 cursor-pointer',
                        p.owner_team_name && p.owner_team_name === currentTeam?.name && 'bg-primary/[0.03]'
                      )}
                    >
                      {/* Player */}
                      {/* SAB-1176: a hard FLOOR, not just the existing cap. With twelve fixed-width
                          siblings this is the only shrinkable cell, so without one it collapses --
                          the handoff records it measuring 94px and rendering "Rha…", and the same
                          class hit My Team twice (19px, then width 0). See
                          docs/runbooks/layout-verification.md. */}
                      <td className="px-3 py-2 max-w-[180px] lg:max-w-none xl:min-w-[200px]">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="h-7 w-7 rounded-full bg-muted/50 overflow-hidden shrink-0">
                            {p.headshot_url ? (
                              <img src={p.headshot_url} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-[9px] text-muted-foreground font-bold">
                                {p.position}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-foreground truncate">{p.full_name}</span>
                              {/* SAB-1172: renders ONLY when this player has active news.
                                  Opens the same detail sheet the row opens, but on News. */}
                              <PlayerNewsIcon
                                sport="nfl"
                                playerId={p.id}
                                playerName={p.full_name}
                                onOpenNews={() => { setDetailTab('news'); setDetailPlayerId(p.id); }}
                              />
                              {/*
                                SAB-964: ONE marker, from the shared resolver. This row used
                                to render TWO badges side by side -- a roster-availability
                                badge and a weekly-injury pill -- on the theory that they are
                                different signals. They are, but a manager scanning a list
                                does not care about the distinction, and only one of the two
                                has ever rendered: injury_status is NULL for all 3,328
                                players until REG week 1, so the second badge was dead space
                                that would have quietly doubled up in week 1.

                                The dropped map also had a `PUP` branch that could never
                                fire -- PUP is a roster state and lives in `status`, which
                                that map never read. That dead branch is the evidence the two
                                signals had already been conflated once.
                              */}
                              <AvailabilityPill injuryStatus={p.injury_status} status={p.status} />
                              {/*
                                SAB-1033. Distinct from AvailabilityPill on purpose: that
                                answers "can he play this week" (injury/roster status), this
                                answers "is he on an NFL roster at all". A cut player used to
                                answer neither, because he was not in the result set.
                              */}
                              {p.active_2026 === false && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap"
                                  title="Not on a current NFL roster. He can still be dropped, and stays on your roster until you do."
                                >
                                  Not on an NFL roster
                                </span>
                              )}
                            </div>
                            {p.nfl_team_abbr && (
                              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                {p.nfl_team_abbr}
                                {weekGames.isBye(p.nfl_franchise_id) ? (
                                  <span className="ml-1 text-[9px] font-bold text-amber-400 bg-amber-400/10 px-1 rounded">BYE</span>
                                ) : gameLine(weekGames.gameFor(p.nfl_franchise_id)) ? (
                                  // This week's game, e.g. "· Sun 1:00 PM vs CIN". Same formatter
                                  // as My Team, so the two surfaces cannot word it differently.
                                  <span data-testid="player-game-line"> · {gameLine(weekGames.gameFor(p.nfl_franchise_id))}</span>
                                ) : null}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-2 py-2">
                        <OwnerActionCell
                          player={p}
                          myTeamName={currentTeam?.name ?? null}
                          canAcquire={canAcquire}
                          onAcquire={() => { void openAcquisition(p); }}
                        />
                      </td>

                      {/* Position */}
                      <td className="text-center px-2 py-2">
                        <Badge variant="outline" className="text-[9px] border-border text-muted-foreground">
                          {p.position}
                        </Badge>
                      </td>

                      {/* SAB-811: PROJ — the shipped board total, the number that orders
                          the draft. null is rendered as a dash, NEVER 0.0: IDP carry no
                          projection for year one and 86 board players have no basis, and a
                          fake 0.0 both misinforms and sorts as if it were real (SAB-809). */}
                      <td className={cn(
                        'text-right px-2 py-2 text-xs font-bold tabular-nums',
                        p.proj_sab_pts == null ? 'text-muted-foreground/50' : 'text-foreground'
                      )}>
                        {p.proj_sab_pts == null ? '--' : Number(p.proj_sab_pts).toFixed(1)}
                      </td>

                      {/* SAB-994: this week's projection, from v_nfl_weekly_projections. */}
                      <td className={cn(
                        'text-right px-2 py-2 text-xs tabular-nums',
                        p.weekly_proj == null ? 'text-muted-foreground/50' : 'text-muted-foreground'
                      )}>
                        {formatWeeklyProj(p.weekly_proj)}
                      </td>

                      {/* SAB-811: ADP — lower is better; ADP 1 is the first pick. */}
                      <td className={cn(
                        'text-right px-2 py-2 text-xs tabular-nums',
                        p.adp == null ? 'text-muted-foreground/50' : 'text-muted-foreground'
                      )}>
                        {p.adp == null ? '--' : Number(p.adp).toFixed(0)}
                      </td>

                      {/* GP — desktop only */}
                      <td className="hidden lg:table-cell text-center px-2 py-2 text-xs text-muted-foreground tabular-nums">
                        {p.games_played}
                      </td>

                      {/* SPTS — always visible */}
                      <td className={cn(
                        'text-right px-2 py-2 text-xs font-bold tabular-nums',
                        p.total_sabotage > 0 ? 'text-damage' : p.total_sabotage < 0 ? 'text-emerald-400' : 'text-muted-foreground'
                      )}>
                        {p.games_played > 0 ? (p.total_sabotage >= 0 ? '+' : '') + Number(p.total_sabotage).toFixed(1) : '--'}
                      </td>

                      {/* AVG — sm+ */}
                      <td className={cn(
                        'hidden sm:table-cell text-right px-2 py-2 text-xs tabular-nums',
                        p.avg_sabotage > 0 ? 'text-damage/80' : p.avg_sabotage < 0 ? 'text-emerald-400/80' : 'text-muted-foreground'
                      )}>
                        {p.games_played > 0 ? (p.avg_sabotage >= 0 ? '+' : '') + Number(p.avg_sabotage).toFixed(1) : '--'}
                      </td>

                      {/* TRS — desktop only */}
                      <td className="hidden lg:table-cell text-right px-2 py-2 text-xs text-damage/60 tabular-nums">
                        {p.games_played > 0 ? '+' + Number(p.total_trash).toFixed(1) : '--'}
                      </td>

                      {/* ANT — desktop only */}
                      <td className="hidden lg:table-cell text-right px-2 py-2 text-xs text-emerald-400/60 tabular-nums">
                        {p.games_played > 0 ? Number(p.total_anti).toFixed(1) : '--'}
                      </td>

                      {/* SAB-1176: the six stat keys. fmtInt applies the dash rule AFTER
                          rounding, so a value that rounds to zero shows a dash rather than
                          a 0 that claims a measurement rounding destroyed. */}
                      {NFL_PLAYER_STAT_COLUMNS.map(c => (
                        <td key={c.key}
                            className="hidden xl:table-cell text-right px-2 py-2 text-xs text-foreground tabular-nums">
                          {p.games_played > 0 ? fmtInt(Number(c.get(p))) : '--'}
                        </td>
                      ))}

                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </ActiveNewsProvider>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-border">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <span className="text-[10px] text-muted-foreground">
                Page {page + 1} of {pageCount}
              </span>
              <button
                onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </Card>
      )}

      {/* ─── Player Detail Modal ─── */}
      <NFLPlayerDetailModal
        onTrade={onTradeWith ? (teamId, pid) => onTradeWith(teamId, pid) : undefined}
        playerId={detailPlayerId}
        initialTab={detailTab}
        open={!!detailPlayerId}
        onOpenChange={(open) => { if (!open) { setDetailPlayerId(null); setDetailTab(undefined); } }}
        // SAB-1045: the SAME row the list badged, so the chip cannot disagree with it.
        waiver={players.find(pl => pl.id === detailPlayerId) ?? undefined}
        // SAB-1045 follow-up: the detail Add opens the SAME flow as the row's button.
        // Not a second implementation -- openAcquisition already routes on the server's
        // waiver_state and hands the final word to nfl-add-drop's refusal.
        onAcquire={() => {
          const p = players.find(pl => pl.id === detailPlayerId);
          if (p) void openAcquisition(p);
        }}
      />

      {/* ─── Add / Waiver Claim Modal ─── */}
      {addModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <Card className="bg-card border-border w-full max-w-md p-5 space-y-4 relative">
            <button onClick={() => { setAddModal(null); setDropPlayerId(''); setFaabBid(0); }} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>

            <div>
              <h3 className="text-base font-display font-bold text-foreground">
                {isOnWaivers ? 'Submit Waiver Claim' : 'Add Free Agent'}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {addModal.full_name} ({addModal.position})
                {addModal.nfl_team_abbr && `, ${addModal.nfl_team_abbr}`}
              </p>
            </div>

            {isOnWaivers && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">FAAB Bid (${faabBudget} remaining)</label>
                <Input
                  type="number"
                  min={0}
                  max={faabBudget}
                  value={faabBid}
                  onChange={e => setFaabBid(Number(e.target.value) || 0)}
                  className="bg-muted/30"
                />
              </div>
            )}

            {rosterFull && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">
                  {isOnWaivers ? 'Drop to make room (required)' : 'Drop a player to make room'}
                </label>
                <select
                  value={dropPlayerId}
                  onChange={e => setDropPlayerId(e.target.value)}
                  className="w-full h-9 rounded-md border border-border bg-muted/30 px-3 text-sm text-foreground"
                >
                  <option value="">Select player to drop...</option>
                  {myRoster
                    .filter(r => !r.position_slot.startsWith('IR'))
                    .sort((a, b) => a.position.localeCompare(b.position) || a.full_name.localeCompare(b.full_name))
                    .map(r => (
                      <option key={r.player_id} value={r.player_id}>
                        {r.full_name} ({r.position}), {r.position_slot}
                      </option>
                    ))
                  }
                </select>
              </div>
            )}

            {isOnWaivers && !rosterFull && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Drop a player (required for waiver claims)</label>
                <select
                  value={dropPlayerId}
                  onChange={e => setDropPlayerId(e.target.value)}
                  className="w-full h-9 rounded-md border border-border bg-muted/30 px-3 text-sm text-foreground"
                >
                  <option value="">Select player to drop...</option>
                  {myRoster
                    .filter(r => !r.position_slot.startsWith('IR'))
                    .sort((a, b) => a.position.localeCompare(b.position) || a.full_name.localeCompare(b.full_name))
                    .map(r => (
                      <option key={r.player_id} value={r.player_id}>
                        {r.full_name} ({r.position}), {r.position_slot}
                      </option>
                    ))
                  }
                </select>
              </div>
            )}

            <Button
              onClick={handleAddOrClaim}
              disabled={submitting || (isOnWaivers && !dropPlayerId) || (rosterFull && !dropPlayerId)}
              className="w-full bg-primary hover:bg-primary/90"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isOnWaivers ? `Submit Claim ($${faabBid} FAAB)` : 'Add Player'}
            </Button>

            {/* SAB-1025: this was "Waivers process Wednesday at midnight PT", and it was
                wrong TWICE over, live, in the one surface where a manager commits money:
                the cron is DAILY, not weekly, and 06:00 UTC is 11:00 PM PT, not midnight.
                So a manager bidding on a Thursday read that resolution was five days away
                when it was that night.

                Now formatted from WAIVER_CRON_UTC, the same constant the settings card
                uses, so both surfaces state one fact and neither can drift from the cron
                again. ET rather than PT so the two agree with each other. */}
            {isOnWaivers && (
              <p className="text-[10px] text-muted-foreground text-center">
                {/* SAB-1142c: was "Waivers process daily at ...". There is no single
                    schedule any more — the two classes are five days apart — so this leads
                    with the DISTINCTION rather than a time. A claim's own date is shown on
                    the claim itself, where it can be read from process_at. */}
                {/* SAB-1142c: the two classes are on DIFFERENT anchors, so the copy states
                    each. The nightly run is a fixed 06:00 UTC cron, so its ET clock time
                    shifts to 1:00 AM after the November changeover; the weekly run is
                    ET-anchored and stays 2:00 AM year-round. A flat "2am ET" would be wrong
                    for four months of the season. */}
                Recently dropped players clear overnight at 2:00 AM ET (1:00 AM once clocks
                change); players whose game has already been played clear on your
                league&apos;s weekly waiver day at 2:00 AM ET. Highest bid wins;
                ties go to the team lowest in the standings.
              </p>
            )}
          </Card>
        </div>
      )}

      {/* SAB-1016: inside the existing wrapper, not after it — the component returns a
          single <div>, and appending a sibling needs a fragment the return never opened. */}
      <NFLAddDropOverlay
        open={!!addDropTarget}
        onOpenChange={(v) => { if (!v) setAddDropTarget(null); }}
        addPlayer={addDropTarget}
        mode={overlayMode}
        faabRemaining={faabBudget}
        rosterSlots={currentLeague?.settings?.roster_slots}
        teamId={currentTeam?.id ?? null}
        leagueId={currentLeague?.id ?? null}
        seasonYear={season ?? null}
        currentWeek={currentWeek ?? null}
        onComplete={(msg) => { toast({ title: 'Roster updated', description: msg }); setLoadAttempt(n => n + 1); }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sort Button                                                        */
/* ------------------------------------------------------------------ */

function SortButton({
  label, sortKey: key, current, dir, onClick,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onClick: (key: SortKey) => void;
}) {
  const isActive = current === key;
  return (
    <button
      onClick={() => onClick(key)}
      className={cn(
        'flex items-center gap-0.5 transition-colors',
        isActive ? 'text-primary' : 'hover:text-foreground'
      )}
    >
      {label}
      {isActive ? (
        dir === 'desc' ? <ArrowDown className="h-2.5 w-2.5" /> : <ArrowUp className="h-2.5 w-2.5" />
      ) : (
        <ArrowUpDown className="h-2.5 w-2.5 opacity-30" />
      )}
    </button>
  );
}
